// Real API service for backend integration
import { demoModeManager } from '../demoModeManager';
import { createDemoApiWrapper } from '../demoApiWrapper';
import { IS_STATIC_DEMO } from '../config';
import { ApiCallOptions } from '../../types/api';
import { setToken, clearToken } from '../../utils/auth';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.lens.aivilo.ai/core/api/v1';

// Assessment endpoints that should always use real API (except in static demo)
const ASSESSMENT_ENDPOINTS = ['/assessments', '/assessment-results'];
const isAssessmentEndpoint = (endpoint: string): boolean => {
  return ASSESSMENT_ENDPOINTS.some(pattern => endpoint.startsWith(pattern));
};

// Real API call function (extracted for wrapper usage)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const realApiCall = async (endpoint: string, options: ApiCallOptions = {}): Promise<any> => {
  // Safety net: in static demo mode, real API should never be called
  if (IS_STATIC_DEMO) {
    throw new Error('Real API calls are disabled in demo mode');
  }

  const token = localStorage.getItem('token');
  const { silentErrors = [], requireAuth = true, ...fetchOptions } = options;

  // If no token and auth is required, redirect to login
  // Exclude onboarding routes because session is created there
  const isOnboardingRoute = globalThis.location.pathname.includes('/assessments/onboard') || globalThis.location.pathname.includes('/assessments/azure');
  if (!token && requireAuth && !isOnboardingRoute && globalThis.location.pathname !== '/login') {
    globalThis.location.href = '/login';
    return null;
  }

  const config: RequestInit = {
    ...fetchOptions,
    headers: {
      'Content-Type': 'application/json',
      ...(fetchOptions.headers || {}),
      ...(token && { 'Authorization': `Bearer ${token}` }),
    }
  };

  try {
    const response = await fetch(`${API_BASE}${endpoint}`, config);

    if (response.status === 401) {
      // Check if this is a Jira-specific error vs general auth error
      const errorData = await response.json().catch(() => ({})) as { message?: string };
      const isJiraError = errorData.message?.toLowerCase().includes('jira');

      if (isJiraError) {
        // This is a Jira integration error, don't clear JWT token
        throw new Error(errorData.message || 'Jira integration error');
      } else {
        // This is a general authentication error, clear tokens and redirect
        clearToken();
        localStorage.removeItem('anonymous_session_token');
        localStorage.removeItem('anonymous_session_client_id');
        localStorage.removeItem('anonymous_session_expires_at');
        if (globalThis.location.pathname !== '/login') {
          globalThis.location.href = '/login';
        }
        return null;
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})) as { detail?: string; message?: string; error?: string };
      // FastAPI typically uses 'detail' field for error messages
      const errorMessage = errorData.detail || errorData.message || errorData.error || `HTTP ${response.status}`;
      const error = Object.assign(new Error(errorMessage), {
        status: response.status,
        response: errorData,
      });
      throw error;
    }

    // Handle 204 No Content responses (no body to parse)
    if (response.status === 204) {
      return null;
    }

    return await response.json();
  } catch (error) {
    // Only log if status is not in silentErrors list
    const err = error as { status?: number; message?: string };
    if (err.status === undefined || !silentErrors.includes(err.status)) {
      console.error(`API call failed: ${endpoint}`, error);
    }
    throw error;
  }
};

// Public apiCall function that supports demo mode
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const apiCall = async (endpoint: string, options: ApiCallOptions = {}): Promise<any> => {
  // Always use real API for assessments (except in static demo mode)
  if (!IS_STATIC_DEMO && isAssessmentEndpoint(endpoint)) {
    return realApiCall(endpoint, options);
  }

  // Check if demo mode is active
  const { isDemoMode, mockDataStore } = demoModeManager.getDemoMode();

  // If in demo mode and mock store is ready, use mock data
  if (isDemoMode && mockDataStore?.isReady) {
    try {
      const wrapper = createDemoApiWrapper(mockDataStore, isDemoMode);
      return await wrapper(realApiCall, endpoint, options);
    } catch (error) {
      if (IS_STATIC_DEMO) {
        // In static demo, never fall back to real API
        return [];
      }
      // Fall back to real API on error
      return realApiCall(endpoint, options);
    }
  }

  // In static demo mode, if store isn't ready yet, return empty data
  if (IS_STATIC_DEMO) {
    return [];
  }

  // Default: use real API
  return realApiCall(endpoint, options);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface ApiResult<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

export { setToken, clearToken };
export { IS_STATIC_DEMO };
